import React, { useState, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import Hidden from '@material-ui/core/Hidden';
import Alert from '@material-ui/lab/Alert';

import TimeZones from 'app/utils/TimeZones';
import Link from 'app/components/Link';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    dividerTop: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    urlInput: {
        textAlign: 'right',
        lineHeight: '1.5'
    },
    rightColumnText: {
        marginBottom: theme.spacing(2)
    }
}));

export default function NewClubRegistrationForm (props) {

    const { onLoading, loading } = props;

    const { app } = useApp();
    const api = useApi();

    const [ values, setValues ] = useState({
        url: "",
        name: "",
        description: "",
        support_email: "",
        timezone: "America/Chicago",
        is_searchable: true,
    })

    const [ errors, setErrors ] = useState({});

    const classes = useStyles();

    useEffect(() => {
        if (app.user && app.user.email && app.user.email != '') {
            setValues({ ...values, support_email: app.user.email });
        }
    }, []);

    const handleChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    const postUrlValidate = () => {
        onLoading(true);

        const data = {
            url: values.url
        };

        api.post('/auth/url/validate', data).catch(error => {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        }).finally(() => onLoading(false));
    }

    const postClubRegistration = () => {
        onLoading(true);
        setErrors({});

        const data = values;

        api.post('/auth/register/club', data).then(res => {

            // Redirect to new club
            window.location = res.redirect;

        }).catch(error => {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
            onLoading(false)
        });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getUrlMessage = () => {
        if (isError('url')) return errors.url;
        return "This will be the URL members and guests use to get to your clubs homepage."
    }

    return (
        <div className="clubRegistrationForm">
            <form noValidate onSubmit={postClubRegistration}>
                <Typography gutterBottom variant="body1" align="center" color="textPrimary">
                    Let's get some basic info about your club to get started. You can continue to customize your club in your club settings later on.
                </Typography>
                <div className={classes.marginTop}/>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl variant="outlined" error={isError('url')} fullWidth>
                            <OutlinedInput
                                id="input-url"
                                value={values.url}
                                onChange={handleChange('url')}
                                onBlur={postUrlValidate}
                                endAdornment={<InputAdornment position="end">.clubapps.org</InputAdornment>}
                                aria-describedby="url-helper-text"
                                labelWidth={0}
                                classes={{ input: classes.urlInput }}
                                placeholder="myclub"
                                color="secondary"
                            />
                            <FormHelperText id="url-helper-text">{getUrlMessage()}</FormHelperText>
                        </FormControl>
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-club-name"
                            label="Club Name"
                            name="name"
                            value={values.name}
                            margin="normal"
                            onChange={handleChange('name')}
                            fullWidth
                            error={isError('name')}
                            helperText={getErrorMessage('name')}
                            variant="outlined"
                            color="secondary"
                        />
                        <TextField
                            id="input-club-description"
                            label="Club Description"
                            name="description"
                            value={values.description}
                            margin="normal"
                            onChange={handleChange('description')}
                            fullWidth
                            error={isError('description')}
                            helperText={getErrorMessage('description')}
                            variant="outlined"
                            multiline
                            rows="4"
                            rowsMax="4"
                            inputProps={{
                                maxLength: "255"
                            }}
                            color="secondary"
                        />
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-club-support-email"
                            label="Support Email"
                            name="support_email"
                            value={values.support_email}
                            margin="normal"
                            onChange={handleChange('support_email')}
                            fullWidth
                            error={isError('support_email')}
                            helperText={getErrorMessage('support_email')}
                            variant="outlined"
                        />
                        <TextField
                            id="input-timezone"
                            label="Club Timezone"
                            name="timezone"
                            value={values.timezone}
                            margin="normal"
                            onChange={handleChange('timezone')}
                            fullWidth
                            error={isError('timezone')}
                            helperText={getErrorMessage('timezone')}
                            variant="outlined"
                            select
                            color="secondary"
                            >
                            {TimeZones.map(option => (
                                <MenuItem key={option.tz} value={option.tz}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox checked={values.is_searchable} onChange={handleChange('is_searchable')} value="is_searchable" color="secondary"/>
                            }
                            label="Allow users to find your club?"
                        />
                    </Grid>
                    <Hidden smDown>
                        <Grid item md={1}/>
                    </Hidden>
                    <Grid item xs={12} md={5}>
                        <Typography className={classes.rightColumnText} gutterBottom variant="body2" color="textSecondary">
                            Use an easy url to remember to encourage your members to check back often.
                        </Typography>
                        <Typography className={classes.rightColumnText} gutterBottom variant="body2" color="textSecondary">
                            You have the option later to upgrade to a subscription and use a custom domain name instead of having to use a clubapps.org url.
                        </Typography>
                        <Typography className={classes.rightColumnText} gutterBottom variant="body2" color="textSecondary">
                            Your club name and description will display to users who can access your club, as well as when users try to find your club if you check the <strong>Find Club</strong> option.
                        </Typography>
                        <Typography className={classes.rightColumnText} gutterBottom variant="body2" color="textSecondary">
                            Choose the timezone that best fits your club, not you. This will be used to display all relative times to members and guests for events, articles, etc.
                        </Typography>
                        <Typography className={classes.rightColumnText} gutterBottom variant="body2" color="textSecondary">
                            Checking the <strong>Find Club</strong> option will allow your club to be displayed on search results when users are trying to connect to an existing club. A club administrator will have to confirm access before the user can access your club.
                        </Typography>
                    </Grid>
                </Grid>
                {isError('general') &&
                    <Alert severity="error" className={classes.marginTop}>
                        {getErrorMessage('general')}
                    </Alert>
                }
                <div className={classes.marginTop}/>
                <Grid container spacing={3} direction="row-reverse">
                    <Grid item xs={12} sm={5}>
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={postClubRegistration}
                            disabled={loading}
                            >
                            Create Club
                        </Button>
                    </Grid>
                    <Hidden xsDown>
                        <Grid item sm={1}/>
                    </Hidden>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                            By continuing, you are agreeing to our <Link to={process.env.MIX_LEGAL_BASEURL + process.env.MIX_LEGAL_ROUTE_ORGANIZATION} target="_blank" color="primary">Organization Terms of Service</Link>.
                        </Typography>
                    </Grid>
                </Grid>
            </form>
        </div>
    );
}
